import React, { useState, useEffect } from 'react';
import LocationDropdown from '../../Dropdown/LocationName';
import MedicalDropdown from '../../Dropdown/MedicalDropdown';

const VehicleModal = ({
  formData,
  onChange,
  onSave,
  editId,
}) => {
  const [validatedFields, setValidatedFields] = useState({
    plate_number: false,
    type: false,
    location_id: false,
    status: false,
    medical_center_id: false,
  });

  const [selectionType, setSelectionType] = useState('location'); // 'location' | 'medical'
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // ───── Session data ─────
  const sessionData = {
    roleName: sessionStorage.getItem('Role_Name'),
    medicalCenterId: sessionStorage.getItem('Medical_Center_Id'),
    medicalCenterName: sessionStorage.getItem('Medical_Center_Name'),
  };

  const isResourceManager = sessionData.roleName === 'EMS Resource Manager';

  // NEW CONDITION you asked for
  const forceMedicalCenterOnly =
    isResourceManager &&
    sessionData.medicalCenterId === '0' &&
    (!sessionData.medicalCenterName || sessionData.medicalCenterName.trim() === '');

  const isSpecificMedicalCenter =
    isResourceManager &&
    sessionData.medicalCenterId &&
    sessionData.medicalCenterId !== '0' &&
    sessionData.medicalCenterId !== '[]';

  // ───── Force selection type when needed ─────
  useEffect(() => {
    if (forceMedicalCenterOnly) {
      setSelectionType('medical');
      // clear location if it was previously selected
      onChange({ target: { name: 'location_id', value: '' } });
    } else if (isSpecificMedicalCenter) {
      setSelectionType('medical');
      onChange({ target: { name: 'medical_center_id', value: sessionData.medicalCenterId } });
      onChange({ target: { name: 'location_id', value: '' } });
    } else if (formData.medical_center_id && formData.medical_center_id !== 0) {
      setSelectionType('medical');
    } else if (formData.location_id && formData.location_id !== 0) {
      setSelectionType('location');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    forceMedicalCenterOnly,
    isSpecificMedicalCenter,
    formData.medical_center_id,
    formData.location_id,
  ]);

  // Default status when editing
  useEffect(() => {
    if (editId && formData.status === undefined) {
      onChange({ target: { name: 'status', value: '0' } });
    }
  }, [editId, formData.status, onChange]);

  // Reset validation when modal opens
  useEffect(() => {
    const modalEl = document.getElementById('bs-vehicles');
    const resetValidation = () => {
      setValidatedFields({
        plate_number: false,
        type: false,
        location_id: false,
        status: false,
        medical_center_id: false,
      });
      setErrors({});
    };

    modalEl?.addEventListener('shown.bs.modal', resetValidation);
    return () => modalEl?.removeEventListener('shown.bs.modal', resetValidation);
  }, []);

  // ───── Handlers ─────
  const handleSelectionTypeChange = (e) => {
    const type = e.target.value;

    // Prevent switching to location when it must be hidden
    if (forceMedicalCenterOnly && type === 'location') return;

    setSelectionType(type);

    if (type === 'location') {
      onChange({ target: { name: 'medical_center_id', value: '' } });
    } else {
      onChange({ target: { name: 'location_id', value: '' } });
    }
  };

  const handleChange = (e) => {
    onChange(e);
    setErrors((prev) => ({ ...prev, [e.target.name]: '' }));
  };

  const handleBlur = (fieldName) => {
    if (!editId && fieldName === 'status') return;
    setValidatedFields((prev) => ({ ...prev, [fieldName]: true }));
  };

  const shouldShowValidation = (fieldName) => {
    if (!editId && fieldName === 'status') return false;
    return validatedFields[fieldName] && !formData[fieldName];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const allFieldsValidated = {
      ...validatedFields,
      plate_number: true,
      type: true,
      status: editId ? true : validatedFields.status,
      location_id: selectionType === 'location',
      medical_center_id: selectionType === 'medical',
    };
    setValidatedFields(allFieldsValidated);

    const newErrors = {};

    if (!formData.plate_number) {
      newErrors.plate_number = 'Plate No. is required';
    }
    if (!formData.type) {
      newErrors.type = 'Type is required';
    }

    if (selectionType === 'location' && (!formData.location_id || formData.location_id === 0)) {
      newErrors.location_id = 'Location is required';
    }
    if (selectionType === 'medical' && (!formData.medical_center_id || formData.medical_center_id === 0)) {
      newErrors.medical_center_id = 'Medical Center is required';
    }
    if (editId && !formData.status) {
      newErrors.status = 'Status is required';
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    try {
      setLoading(true);
      await onSave();
    } finally {
      setLoading(false);
    }
  };

  // ───── Render ─────
  return (
    <div
      id="bs-vehicles"
      className="modal fade"
      tabIndex="-1"
      aria-labelledby="bs-vehicles"
      style={{ display: 'none' }}
      aria-hidden="true"
    >
      <div className="modal-dialog modal-dialog-scrollable modal-lg">
        <div className="modal-content">
          <div className="modal-header d-flex align-items-center">
            <h5 className="modal-title">{editId ? 'Edit Vehicle' : 'Add Vehicle'}</h5>
            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
          </div>

          <div className="modal-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                {/* Plate Number */}
                <div className="col-md-6 mb-3">
                  <label>Plate No. <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className={`form-control ${shouldShowValidation('plate_number') ? 'is-invalid' : ''}`}
                    name="plate_number"
                    value={formData.plate_number || ''}
                    onChange={handleChange}
                    onBlur={() => handleBlur('plate_number')}
                    placeholder="Enter Plate Number"
                  />
                  {shouldShowValidation('plate_number') && (
                    <div className="invalid-feedback">Plate No. is required</div>
                  )}
                </div>

                {/* Type */}
                <div className="col-md-6 mb-3">
                  <label>Type <span className="text-danger">*</span></label>
                  <select
                    className={`form-select ${shouldShowValidation('type') ? 'is-invalid' : ''}`}
                    name="type"
                    value={formData.type || ''}
                    onChange={handleChange}
                    onBlur={() => handleBlur('type')}
                  >
                    <option value="">Select Type</option>
                    <option value="ACLS">ACLS</option>
                    <option value="BLS">BLS</option>
                  </select>
                  {shouldShowValidation('type') && <div className="invalid-feedback">Type is required</div>}
                </div>

                {/* Selection Type Radio Buttons */}
                <div className="col-12 mb-3">
                  <label className="me-3">
                    {forceMedicalCenterOnly || isSpecificMedicalCenter
                      ? 'Medical Center *'
                      : 'Choose Location or Medical Center *'}
                  </label>

                  {/* Hide Location radio completely when forced to Medical Center */}
                  {!forceMedicalCenterOnly && !isSpecificMedicalCenter && (
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="selectionType"
                        id="locationRadio"
                        value="location"
                        checked={selectionType === 'location'}
                        onChange={handleSelectionTypeChange}
                      />
                      <label className="form-check-label" htmlFor="locationRadio">
                        Location
                      </label>
                    </div>
                  )}

                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="selectionType"
                      id="medicalRadio"
                      value="medical"
                      checked={selectionType === 'medical'}
                      onChange={handleSelectionTypeChange}
                      disabled={forceMedicalCenterOnly} // optional visual cue
                    />
                    <label className="form-check-label" htmlFor="medicalRadio">
                      Medical Center
                    </label>
                  </div>
                </div>

                {/* Conditional Dropdown */}
                {selectionType === 'location' ? (
                  <div className="col-md-6 mb-3">
                    <label>Location <span className="text-danger">*</span></label>
                    <LocationDropdown
                      name="location_id"
                      value={formData.location_id || ''}
                      onChange={handleChange}
                      className={errors.location_id ? 'is-invalid' : ''}
                    />
                    {errors.location_id && <div className="invalid-feedback d-block">{errors.location_id}</div>}
                  </div>
                ) : (
                  <div className="col-md-6 mb-3">
                    <label>Medical Center <span className="text-danger">*</span></label>
                    <MedicalDropdown
                      value={
                        formData.medical_center_id ||
                        (isSpecificMedicalCenter ? sessionData.medicalCenterId : '')
                      }
                      onChange={(e) => {
                        handleChange(e);
                        setErrors((prev) => ({ ...prev, medical_center_id: '' }));
                      }}
                      disabled={isSpecificMedicalCenter}
                    />
                    {errors.medical_center_id && (
                      <div className="text-danger">{errors.medical_center_id}</div>
                    )}
                  </div>
                )}

                {/* Status – only on edit */}
                {editId && (
                  <div className="col-md-6 mb-3">
                    <label>Status <span className="text-danger">*</span></label>
                    <select
                      className={`form-select ${shouldShowValidation('status') ? 'is-invalid' : ''}`}
                      name="status"
                      value={formData.status ?? '0'}
                      onChange={handleChange}
                      onBlur={() => handleBlur('status')}
                    >
                      <option value="">Select Status</option>
                      <option value="0">Busy</option>
                      <option value="1">Ready for Service</option>
                      <option value="2">Under Maintenance</option>
                    </select>
                    {shouldShowValidation('status') && (
                      <div className="invalid-feedback">Status is required</div>
                    )}
                  </div>
                )}
              </div>
            </form>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-outline-danger"
              data-bs-dismiss="modal"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <span className="spinner-border spinner-border-sm me-2" role="status" />
              ) : editId ? (
                'Update'
              ) : (
                'Save'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleModal;